'use client'

import dynamic from 'next/dynamic'
import { Component, useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { EditionOrientation } from '@/components/product/EditionObjectScene'
import { EDITION_VIEW } from '@/lib/edition-turntable'
import { POSTER } from '@/lib/media'
import styles from './page.module.css'

/**
 * "The object, in the round." The reveal poster is the server-rendered baseline
 * and stays the whole motion study for every visitor without JavaScript, with
 * Save-Data on, with reduced motion requested, without a usable WebGL context,
 * or on a software rasteriser. Capable browsers upgrade in place to the live
 * set — the bottle, the black tray, its foam insert, and the supplied sliding
 * sleeve — which the visitor can actually turn by dragging or with the arrow
 * keys, rather than watch a pre-rendered loop.
 *
 * The still is never traded for a promise: it stays mounted and lit through the
 * entire loading window and retires only once the renderer reports a frame it
 * has really drawn. Any failure after the upgrade decision — a GLB that will
 * not load, a renamed export, a context the driver takes back, a render throw —
 * brings it straight back.
 *
 * The stage owns every DOM concern (capability tiering, visibility gating,
 * pointer and keyboard input, the controls) and hands the renderer nothing but
 * booleans and one mutable orientation ref. The canvas region is decorative and
 * `aria-hidden`: everything it shows already exists as text in the record above
 * it, and each control reports its own state through `aria-pressed`, so the
 * page gains no second status announcement.
 */

const EditionObjectScene = dynamic(() => import('@/components/product/EditionObjectScene'), { ssr: false })

/** Live-canvas readiness, mirrored to `data-scene` for deterministic checks. */
type SceneState = 'still' | 'loading' | 'ready' | 'fallback'

/**
 * Generous on purpose: this budget only exists to stop a canvas that will never
 * paint from sitting over the study, and a cold cache on a slow connection (or
 * a development server compiling the three.js graph for the first time) can
 * take far longer than a page load.
 */
const LOAD_BUDGET_MS = 30000
/** Fresh canvases granted after a lost context before the still wins. */
const MAX_CONTEXT_ATTEMPTS = 2
/**
 * CPU rasterisers (headless CI, VMs without GPU passthrough, machines that fell
 * back from a blocklisted driver) report themselves here. They can create a
 * context and then need tens of seconds to compile a PBR shader, so the live
 * set would sit blank in the frame and eventually lose the context: the
 * authored still is both faster and better on those machines.
 */
const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software rasterizer|microsoft basic render/i

const DESCRIPTION =
  'MIST by BDQ Edition 001 Peridot on a black tray beside its sliding sleeve, catching light across the faceted peridot cap and gold collar.'

type DragState = { pointerId: number; x: number; y: number; pointerType: string }

/**
 * The GLB and the WebGL context are the two things that can fail after the
 * upgrade decision. Either one throwing must restore the authored still rather
 * than leave an empty frame under the motion-study heading, so the live scene
 * renders behind a boundary that reports the failure and unmounts itself.
 */
class SceneBoundary extends Component<{ onFailure: (reason: string) => void; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override componentDidCatch(error: Error) {
    this.props.onFailure(`render:${error.message.slice(0, 120)}`)
  }

  override render() {
    return this.state.failed ? null : this.props.children
  }
}

export default function EditionTurntable() {
  const stageRef = useRef<HTMLDivElement>(null)
  const orientationRef = useRef<EditionOrientation>({ targetYaw: 0, targetPitch: 0, pointerX: 0, pointerY: 0 })
  const requestFrameRef = useRef<(() => void) | null>(null)
  const dragRef = useRef<DragState | null>(null)
  // Canvas generation: bumping it hands the scene a brand new canvas element.
  const attemptRef = useRef(0)

  const [enhanced, setEnhanced] = useState(false)
  const [motionOk, setMotionOk] = useState(false)
  const [scene, setScene] = useState<SceneState>('still')
  const [paused, setPaused] = useState(false)
  const [inView, setInView] = useState(false)
  const [documentVisible, setDocumentVisible] = useState(true)
  const [attempt, setAttempt] = useState(0)

  // Capability tier, decided once. The live set costs a WebGL context, a GLB,
  // and a shader compile, so Save-Data, context-less browsers, and CPU
  // rasterisers keep the authored still.
  useEffect(() => {
    let usable = false
    try {
      const probe = document.createElement('canvas')
      const context = probe.getContext('webgl2') ?? probe.getContext('webgl')
      if (context) {
        const info = context.getExtension('WEBGL_debug_renderer_info')
        const renderer = info === null ? '' : String(context.getParameter(info.UNMASKED_RENDERER_WEBGL))
        usable = !SOFTWARE_RENDERER.test(renderer)
      }
      // The probe must not keep a context alive: contexts are a scarce
      // per-document resource.
      context?.getExtension('WEBGL_lose_context')?.loseContext()
    } catch {
      usable = false
    }
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    if (!usable || connection?.saveData === true) return
    setEnhanced(true)
  }, [])

  // The study is ambient product motion, so a reduced-motion request keeps the
  // still — and keeps it live: flipping the system setting mid-visit retires the
  // canvas rather than waiting for a reload.
  useEffect(() => {
    if (!enhanced) return
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const applyMotion = () => setMotionOk(!query.matches)
    applyMotion()
    query.addEventListener('change', applyMotion)
    return () => query.removeEventListener('change', applyMotion)
  }, [enhanced])

  // Off-screen and background tabs must not pay for a frame — and must not hold
  // a WebGL context either: contexts are capped per document and this page is
  // not the only surface that wants one.
  useEffect(() => {
    if (!enhanced || !motionOk) return
    const target = stageRef.current
    if (!target) return
    const observer = new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)), { rootMargin: '120px' })
    observer.observe(target)
    const onVisibility = () => setDocumentVisible(document.visibilityState === 'visible')
    onVisibility()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enhanced, motionOk])

  /**
   * A lost context is recoverable and a dead canvas is not: a canvas whose
   * context was lost — including one force-lost by R3F's own teardown, which is
   * what React's development double-effect provokes — never gets it back, so
   * the fix is a brand new canvas element. A bounded number of fresh canvases
   * is granted, and the stage does not change its reported state while it
   * retries: dropping back to `loading` would blink the controls and the poster
   * in and out for the few hundred milliseconds the replacement takes. Every
   * other failure (a renamed export, a render throw, a load that never lands)
   * is not transient and goes straight to the still.
   */
  const onFailure = useCallback((reason: string) => {
    if (reason === 'context-lost' && attemptRef.current < MAX_CONTEXT_ATTEMPTS) {
      attemptRef.current += 1
      setAttempt(attemptRef.current)
      return
    }
    setScene((current) => (current === 'fallback' ? current : 'fallback'))
    if (process.env.NODE_ENV !== 'production') console.warn(`[EditionTurntable] live view unavailable: ${reason}`)
  }, [])

  const onReady = useCallback(() => setScene((current) => (current === 'fallback' ? current : 'ready')), [])

  const live = enhanced && motionOk && scene !== 'fallback'
  // The context only exists while the study is actually on screen in a
  // foregrounded tab; leaving the viewport releases it back to the browser.
  const mounted = live && inView && documentVisible
  // Controls only exist once the canvas has actually painted: nothing here is
  // interactive while the poster is the only thing in the frame.
  const ready = live && scene === 'ready'
  const ambient = mounted && ready && !paused

  // Entering the upgrade path starts the loading window; leaving it (a
  // reduced-motion request, a failure) puts the still back in charge.
  useEffect(() => {
    setScene((current) => {
      if (current === 'fallback') return current
      if (!live) return 'still'
      return current === 'still' ? 'loading' : current
    })
  }, [live])

  // A never-resolving asset would otherwise hold a blank canvas over the still.
  useEffect(() => {
    if (scene !== 'loading' || !mounted) return
    const timer = window.setTimeout(() => onFailure('load-timeout'), LOAD_BUDGET_MS)
    return () => window.clearTimeout(timer)
  }, [scene, mounted, onFailure])

  // Leaving the viewport tears the canvas down, so readiness must go with it:
  // the next mount has to paint a frame before the stage calls itself live
  // again, otherwise the controls would sit over a blank canvas for a beat.
  useEffect(() => {
    if (mounted || scene !== 'ready') return
    setScene('loading')
  }, [mounted, scene])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary) return
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, pointerType: event.pointerType }
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const orientation = orientationRef.current
    const drag = dragRef.current
    if (drag && drag.pointerId === event.pointerId) {
      const turned = orientation.targetYaw + (event.clientX - drag.x) * EDITION_VIEW.dragYawPerPx
      orientation.targetYaw = Math.min(Math.max(turned, -EDITION_VIEW.yawLimit), EDITION_VIEW.yawLimit)
      // Touch keeps its vertical axis for scrolling (`touch-action: pan-y`), so
      // only mouse and pen drags tilt the view.
      if (drag.pointerType !== 'touch') {
        const tilted = orientation.targetPitch + (event.clientY - drag.y) * EDITION_VIEW.dragPitchPerPx
        orientation.targetPitch = Math.min(Math.max(tilted, EDITION_VIEW.pitchMin), EDITION_VIEW.pitchMax)
      }
      drag.x = event.clientX
      drag.y = event.clientY
    } else if (event.pointerType === 'mouse') {
      const rect = event.currentTarget.getBoundingClientRect()
      orientation.pointerX = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1
      orientation.pointerY = 1 - ((event.clientY - rect.top) / Math.max(rect.height, 1)) * 2
    }
    requestFrameRef.current?.()
  }, [])

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  const onPointerLeave = useCallback(() => {
    orientationRef.current.pointerX = 0
    orientationRef.current.pointerY = 0
    requestFrameRef.current?.()
  }, [])

  // The keyboard equivalent of the drag turntable.
  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const orientation = orientationRef.current
    switch (event.key) {
      case 'ArrowLeft':
        orientation.targetYaw = Math.max(orientation.targetYaw - EDITION_VIEW.keyTurn, -EDITION_VIEW.yawLimit)
        break
      case 'ArrowRight':
        orientation.targetYaw = Math.min(orientation.targetYaw + EDITION_VIEW.keyTurn, EDITION_VIEW.yawLimit)
        break
      case 'ArrowUp':
        orientation.targetPitch = Math.min(orientation.targetPitch + EDITION_VIEW.keyTilt, EDITION_VIEW.pitchMax)
        break
      case 'ArrowDown':
        orientation.targetPitch = Math.max(orientation.targetPitch - EDITION_VIEW.keyTilt, EDITION_VIEW.pitchMin)
        break
      case 'Home':
        orientation.targetYaw = 0
        orientation.targetPitch = 0
        break
      default:
        return
    }
    event.preventDefault()
    requestFrameRef.current?.()
  }, [])

  const recentre = useCallback(() => {
    const orientation = orientationRef.current
    orientation.targetYaw = 0
    orientation.targetPitch = 0
    orientation.pointerX = 0
    orientation.pointerY = 0
    requestFrameRef.current?.()
  }, [])

  return (
    <figure className={styles.turntable}>
      <div
        ref={stageRef}
        className={styles.turntableStage}
        data-scene={scene}
        data-motion={live ? (paused ? 'paused' : 'running') : 'off'}
      >
        {/*
          * The poster and the canvas share this frame, and the frame alone owns
          * the media box: the still in flow gives it its height and the live
          * surface fills it. The control rail is a sibling, so on a narrow
          * screen it can drop below the frame without stretching the canvas
          * over its own buttons.
          */}
        <div className={styles.turntableFrame}>
          <picture>
            <source type="image/avif" srcSet={`${POSTER.mobile.avif} 720w, ${POSTER.desktop.avif} 1280w`} />
            <source type="image/webp" srcSet={`${POSTER.mobile.webp} 720w, ${POSTER.desktop.webp} 1280w`} />
            <img
              className={styles.turntableMedia}
              src={POSTER.desktop.webp}
              width={POSTER.desktop.width}
              height={POSTER.desktop.height}
              loading="lazy"
              alt={DESCRIPTION}
            />
          </picture>

          {mounted ? (
            <div
              className={styles.turntableSurface}
              data-visible={ready ? 'true' : undefined}
              // Until the first frame has painted there is nothing live to turn,
              // so the surface is neither focusable nor announced.
              role={ready ? 'img' : undefined}
              aria-hidden={ready ? undefined : true}
              aria-label={ready ? `Live view. ${DESCRIPTION} Drag, or use the arrow keys, to turn it.` : undefined}
              tabIndex={ready ? 0 : -1}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onLostPointerCapture={endDrag}
              onPointerLeave={onPointerLeave}
              onKeyDown={onKeyDown}
            >
              <div className={styles.turntableCanvas} aria-hidden="true">
                <SceneBoundary key={attempt} onFailure={onFailure}>
                  <EditionObjectScene
                    ambient={ambient}
                    orientationRef={orientationRef}
                    requestFrameRef={requestFrameRef}
                    onReady={onReady}
                    onFailure={onFailure}
                  />
                </SceneBoundary>
              </div>
            </div>
          ) : null}
        </div>

        {mounted && ready ? (
          <div className={styles.turntableControls}>
            <button type="button" className={styles.turntableControl} data-emphasis="primary" onClick={recentre}>
              <span className={styles.turntableControlIndex}>01</span>
              <span>Recentre the object</span>
            </button>
            <button
              type="button"
              className={styles.turntableControl}
              aria-pressed={paused}
              onClick={() => setPaused((current) => !current)}
            >
              <span className={styles.turntableControlIndex}>02</span>
              <span>{paused ? 'Resume motion' : 'Pause motion'}</span>
            </button>
            <p className={styles.turntableHint} aria-hidden="true">
              Drag to turn — arrow keys once focused
            </p>
          </div>
        ) : null}
      </div>
      <figcaption className={styles.turntableCaption}>
        The object, in the round — supplied in a sliding sleeve over a black tray. No hinged box.
      </figcaption>
    </figure>
  )
}
